import { z } from "zod";
import { respostasSchema } from "./respostas";

export const STATUS_DA_ANAMNESE = ["RASCUNHO", "ENVIADA", "RESPONDIDA", "REVISADA", "CANCELADA"] as const;
export type StatusDaAnamnese = (typeof STATUS_DA_ANAMNESE)[number];

export const ROTULO_DO_STATUS_DA_ANAMNESE: Record<StatusDaAnamnese, string> = {
  RASCUNHO: "Em preenchimento",
  ENVIADA: "Aguardando o paciente",
  RESPONDIDA: "Aguardando revisão",
  REVISADA: "Revisada",
  CANCELADA: "Cancelada",
};

export const QUEM_RESPONDEU = ["PACIENTE", "RESPONSAVEL", "EQUIPE"] as const;
export type QuemRespondeu = (typeof QUEM_RESPONDEU)[number];

const id = z.string().min(1);
const nome = z.string().trim().min(3, "Digite o nome completo").max(160);

/** Responsável legal: obrigatório quando quem responde não é o próprio paciente. */
const comResponsavel = <T extends z.ZodRawShape>(shape: T) =>
  z
    .object({
      ...shape,
      respondidaPor: z.enum(["PACIENTE", "RESPONSAVEL"]),
      responsavelNome: z.string().trim().max(160).nullish(),
      responsavelParentesco: z.string().trim().max(60).nullish(),
    })
    // O zod 4 não resolve o tipo do objeto com o shape genérico T; os três campos daqui existem sempre.
    .refine((objeto) => {
      const v = objeto as unknown as { respondidaPor: string; responsavelNome?: string | null };
      return v.respondidaPor !== "RESPONSAVEL" || (!!v.responsavelNome && v.responsavelNome.length >= 3);
    }, {
      message: "Diga o nome do responsável",
      path: ["responsavelNome"],
    });

// ── Equipe ──
export const anamneseIdSchema = z.object({ id });
export const criarAnamneseSchema = z.object({ patientId: id });
export const salvarAnamneseSchema = z.object({ id, respostas: respostasSchema });
export const concluirAnamneseNaClinicaSchema = z.object({ id, respostas: respostasSchema });
export const revisarAnamneseSchema = z.object({ id, observacao: z.string().trim().max(2000).nullish() });
export const adendoDaAnamneseSchema = z.object({ id, texto: z.string().trim().min(2, "Escreva a correção").max(2000) });
export const enviarAnamneseSchema = z.object({ patientId: id });

// ── Paciente (link público) ──
const token = z.string().min(20).max(100);
const acesso = z.string().min(20).max(100);
export const abrirAnamnesePublicaSchema = z.object({ token });
export const verificarAnamnesePublicaSchema = z.object({ token, nascimento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inválida") });
export const carregarAnamnesePublicaSchema = z.object({ acesso });
export const salvarAnamnesePublicaSchema = z.object({ acesso, respostas: respostasSchema });
export const enviarAnamnesePublicaSchema = comResponsavel({
  acesso,
  respostas: respostasSchema,
  assinaturaNome: nome,
  ciencia: z.literal(true, { error: "Confirme que leu o aviso de privacidade" }),
  declaracao: z.literal(true, { error: "Confirme que as informações são verdadeiras" }),
});
